import { useRef, useState } from 'react';
import {
  Grid,
  GridColumn as Column,
  GridNoRecords
} from '@progress/kendo-react-grid';
import { State, toDataSourceRequestString } from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import Toolbar from '../components/page-toolbar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { GetReportsAllUserPermissionsAsync } from '../services/allusers';
import { appStore } from '../stores/app-store';
import { useGetAdminUsers } from '../hooks/services/use-users';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'username', dir: 'asc' }],
  ...DefaultGridSettings.initialDataState
};

const AllUserPermissionsGrid = () => {
  const [dataState, setDataState] = useState<State>({
    ...initialDataState
  });

  const { data, isLoading, mutate } = useGetAdminUsers(
    toDataSourceRequestString(dataState),
    true
  );

  const gridExportRef = useRef<ExcelExport | null>(null);

  const onExportExcel = async () => {
    appStore.showLoading();
    try {
      await GetReportsAllUserPermissionsAsync().then((response) => {
        if (gridExportRef.current !== null) {
          gridExportRef.current.save(response);
        }
      });
    } catch (error) {
      console.error(error);
    } finally {
      appStore.hideLoading();
    }
  };

  return (
    <>
      <Toolbar title={'All users permissions'} />
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search users'}
          columnsToSearch={['username', 'name']}
          showCardMode={false}
          exportRef={gridExportRef}
          useCustomExportExcel={true}
          handleCustomExportToExcel={onExportExcel}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport ref={gridExportRef} fileName={`allusers-permissions.xlsx`}>
          <ExcelExportColumn field="username" title="Username" width={250} />
          <ExcelExportColumn field={'name'} title={'Name'} width={250} />
          <ExcelExportColumn
            field="myOrganisation"
            title="My organisation"
            width={270}
          />
          <ExcelExportColumn field="myNetwork" title="My network" width={250} />
          <ExcelExportColumn field="myEvents" title="My events" width={150} />
          <ExcelExportColumn field="myLibrary" title="My library" width={250} />
          <ExcelExportColumn
            field={'globalLibrary'}
            title={'Global library'}
            width={250}
          />
          <ExcelExportColumn field={'reports'} title={'Reports'} width={150} />
          <ExcelExportColumn field={'admin'} title={'Admin'} width={150} />
        </ExcelExport>

        <Grid
          id={'grid-users-permissions'}
          style={{
            flexGrow: 1,
            minHeight: 0,
            height: '100%',
            overflow: 'hidden'
          }}
          pageable={DefaultGridSettings.pagerSettings}
          sortable={true}
          data={data}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}>
          <Column
            field={'username'}
            filter={'text'}
            title={'Username'}
            columnMenu={ColumnMenu}
          />
          <Column field={'name'} title={'Name'} columnMenu={ColumnMenu} />
          <Column
            field={'myOrganisation'}
            title={'My organisation'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'myNetwork'}
            title={'My network'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'myEvents'}
            title={'My events'}
            columnMenu={ColumnMenu}
            width={140}
          />
          <Column
            field={'myLibrary'}
            title={'My library'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'globalLibrary'}
            title={'Global library'}
            columnMenu={ColumnMenu}
          />
          <Column
            field={'reports'}
            title={'Reports'}
            columnMenu={ColumnMenu}
            width={120}
          />
          <Column
            field={'admin'}
            title={'Admin'}
            columnMenu={ColumnMenu}
            width={120}
          />

          <GridNoRecords>
            {isLoading ? (
              <div
                className={
                  'd-flex flex-column align-items-center gap-2 text-muted'
                }>
                <Loader type={'pulsing'} />
                <span>Loading data</span>
              </div>
            ) : (
              <NoRecords />
            )}
          </GridNoRecords>
        </Grid>
      </div>
    </>
  );
};
export default AllUserPermissionsGrid;
